import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { memo, useCallback, useMemo } from "react";
import {
  AutoComplete,
  AutoCompleteMulti,
  FormContainer,
  FormInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import {
  TIntegrationRuleFieldForm,
  integrationRuleFieldSchema,
} from "./integrationRuleFieldSchema";
import { IIntegrationRuleFieldProps } from "./models";
import {
  automationFieldList,
  createIntegrationRuleField,
  getIntegrationRuleFieldById,
  softwareListForIntegrationRuleFields,
  updateIntegrationRuleFieldById,
} from "../../../../../../../services/integrationRuleField.service";

const translationPath = "page.4uBus.integrationRule.integrationRuleField.";

export const IntegrationRuleField = memo(
  ({ id, busIntegrationRuleId, onBackButton }: IIntegrationRuleFieldProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createIntegrationRuleField,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateIntegrationRuleFieldById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getIntegrationRuleFieldById,
    );

    const saveHandler = (v: TIntegrationRuleFieldForm) =>
      save({
        _4uBusIntegrationRuleId: busIntegrationRuleId ?? "",
        automationFieldId: v.automationField!.id,
        individualExternalId: v.individualExternalId,
        softwareIds: v.softwares?.map((software) => software.id) ?? [],
      });

    const updateHandler = (v: TIntegrationRuleFieldForm, id: string) =>
      update({
        item: {
          id: id,
          automationFieldId: v.automationField!.id,
          individualExternalId: v.individualExternalId,
          softwareIds: v.softwares?.map((software) => software.id) ?? [],
        },
        id: id,
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TIntegrationRuleFieldForm>> => {
        const { data, success } = await get(id);
        if (data && success) {
          const newData: TIntegrationRuleFieldForm = {
            ...data,
            automationField: data.automationField,
            softwares:
              data._4uBusIntegrationRuleFieldSoftwares?.map(
                (item) => item.software,
              ) ?? [],
          };
          return { data: newData, success: true };
        }

        return { data, success };
      },
      [get],
    );

    const automationFieldParams = useMemo(
      () => ({
        _4uBusIntegrationRuleId: busIntegrationRuleId,
      }),
      [busIntegrationRuleId],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TIntegrationRuleFieldForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "integration_rule_field")}
        schema={integrationRuleFieldSchema}
        subtitle={t(translationPath + "new_integration_rule_field")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        defaultValues={{
          softwares: [],
        }}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="error" fontWeight="bold">
                {t(translationPath + "if")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormInput
                size="small"
                name="individualExternalId"
                fullWidth
                label={t(translationPath + "individual_external_id")}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" color="error" fontWeight="bold">
                {t(translationPath + "then")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <AutoComplete
                request={automationFieldList}
                params={automationFieldParams}
                getOptionLabel={({ name }) => name}
                name="automationField"
                size="small"
                label={t(translationPath + "automation_field")}
              />
            </Grid>

            <Grid item xs={12}>
              <AutoCompleteMulti
                request={softwareListForIntegrationRuleFields}
                getOptionLabel={({ name }) => name}
                name="softwares"
                size="small"
                label={t(translationPath + "softwares")}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  },
);
